// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { DnaSyngoviaConnectionRect } from '../../interfaces/dna-syngovia-connection-rect';
import { DnaSyngoviaGroupNodeEType } from '../../interfaces/dna-syngovia-group-node';
import { ConnectionsRectByType } from './connections-rect.service';

export abstract class AbstractChartMapService {
  protected readonly PADDING: number = 8;
  protected readonly PADDING_VERTICAL: number = 42;
  protected readonly ENTRY_SEGMENT_SMALL: number = 12;
  protected readonly ENTRY_SEGMENT_MEDIUM: number = 28;
  public readonly NUM_OF_COLUMNS = 4;
  private readonly SERVER_COUNT_LIMIT = 3;
  private readonly SERVER_ADDITION_HEIGHT = 102;

  protected readonly dimensionsCardDictionary: {
    [key: string]: { width: number; height: number };
  } = {
    server: { width: 252, height: 408 },
    modality: { width: 108, height: 108 },
    digital_product: { width: 108, height: 108 },
    client: { width: 108, height: 108 },
    dicom_proxy: { width: 108, height: 108 },
  };

  protected getWidthPadding(groupNodeType: string): number {
    return this.dimensionsCardDictionary[groupNodeType].width + this.PADDING;
  }

  protected getHeightPadding(
    groupNodeType: string,
    padding = this.PADDING_VERTICAL,
  ): number {
    return this.dimensionsCardDictionary[groupNodeType].height + padding;
  }

  protected heightByRow(row: number, type: string): number {
    let y = 0;
    for (let r = 0; r < row; r++) {
      y += this.getHeightPadding(
        type,
        r % 2 ? this.PADDING : this.PADDING_VERTICAL,
      );
    }

    return y;
  }

  protected createDOMRectInstance(
    x: number,
    y: number,
    groupNodeType: string,
    height?: number,
  ): DOMRect {
    return new DOMRect(
      x,
      y,
      this.dimensionsCardDictionary[groupNodeType].width,
      height ?? this.dimensionsCardDictionary[groupNodeType].height,
    );
  }

  public chartWidth(): number {
    return (
      this.NUM_OF_COLUMNS * this.dimensionsCardDictionary.modality.width +
      this.NUM_OF_COLUMNS * this.dimensionsCardDictionary.digital_product.width +
      this.dimensionsCardDictionary.server.width +
      2 * this.NUM_OF_COLUMNS * this.PADDING
    );
  }

  public chartHeight(connectionsRectByType: ConnectionsRectByType): number {
    const rightHeight = this.rightSideHeight(connectionsRectByType);
    const leftHeight = this.leftSideHeight(connectionsRectByType);
    const centerHeight = this.serverHeight(connectionsRectByType);

    return Math.max(rightHeight, centerHeight, leftHeight);
  }

  public serverHeight(connectionsRectByType: ConnectionsRectByType): number {
    const height = Math.max(
      this.leftSideHeight(connectionsRectByType),
      this.rightSideHeight(connectionsRectByType),
      this.serverHeightByNodesQuantity(connectionsRectByType),
    );

    return height;
  }

  private serverHeightByNodesQuantity(
    connectionsRectByType: ConnectionsRectByType,
  ) {
    const serverCon = connectionsRectByType[DnaSyngoviaGroupNodeEType.SERVER];
    if (serverCon?.length === 0) {
      return this.dimensionsCardDictionary.server.height;
    }

    const serverNodesLength = serverCon[0].connection.groupNode.nodes.length;
    const factor = Math.ceil((serverNodesLength - this.SERVER_COUNT_LIMIT) / 2);

    let serverHeight = this.dimensionsCardDictionary.server.height;
    if (serverNodesLength > this.SERVER_COUNT_LIMIT) {
      serverHeight += this.SERVER_ADDITION_HEIGHT * factor;
    }

    return serverHeight;
  }

  private leftSideHeight(connectionsRectByType: ConnectionsRectByType): number {
    const digitalProductsRects =
      connectionsRectByType[DnaSyngoviaGroupNodeEType.DIGITAL_PRODUCT];
    const clientRects = connectionsRectByType[DnaSyngoviaGroupNodeEType.CLIENT];
    const dicomProxyRects =
      connectionsRectByType[DnaSyngoviaGroupNodeEType.DICOM_PROXY];

    let rows = Math.ceil(
      (digitalProductsRects?.length ?? 0) / this.NUM_OF_COLUMNS,
    );

    if (clientRects?.length > 0) {
      rows++;
    }
    if (dicomProxyRects?.length > 0) {
      rows++;
    }

    const height = this.heightByRow(rows, DnaSyngoviaGroupNodeEType.CLIENT);

    return height - this.PADDING;
  }

  private rightSideHeight(
    connectionsRectByType: ConnectionsRectByType,
  ): number {
    const rects = connectionsRectByType[DnaSyngoviaGroupNodeEType.MODALITY];
    const rows = Math.ceil((rects.length ?? 0) / this.NUM_OF_COLUMNS);

    const height = this.heightByRow(rows, DnaSyngoviaGroupNodeEType.MODALITY);

    return height - this.PADDING;
  }

  public abstract calculateRectValues(
    rects: DnaSyngoviaConnectionRect[],
    row?: number,
    column?: number,
    height?: number,
  ): { row: number; column: number } | void;

  protected abstract calculateLinePath(
    rect: DnaSyngoviaConnectionRect,
    row?: number,
    column?: number,
  ): string | null;
}
